<script setup lang="ts">
import {
  GpBtn,
  GpModal,
  GpTabBar,
  type GpToastProps,
  useFaIcons,
  useGpToasts,
} from '@gportal/uikit';
import { type Image } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { computed, inject, ref, toRef, watchEffect } from 'vue';
import {
  faCheck,
  faCompactDisc,
  faCopy,
  faFolderOpen,
  faUpload,
} from '@fortawesome/pro-solid-svg-icons';
import ModalFooterBtn from 'gpcore-uikit/components/new/ModalFooterBtn.vue';
import useGlobalConfig from '@panel/composables/useGlobalConfig';
import { UploadFileInjectKey } from '@panel/plugins/symbols';
import { type UploadPlugin } from '@panel/plugins/types';
import UploadToastContent from './UploadToastContent.vue';

export type UploadImageMeta = {
  id: string;
};

useFaIcons({ faUpload });

const props = defineProps<{
  show: boolean;
  image?: Image;
}>();

const emit = defineEmits<{
  close: [];
  upload: [];
}>();

const config = useGlobalConfig();

const showRef = toRef(props, 'show');

const snippet = computed(
  () =>
    `
curl -X POST \

    https://${config.gateway.host}/image/upload/${props.image.id} \

    -H 'Content-Type: application/octet-stream' \

    -H 'Authorization: Bearer ${props.image.imageUpload.token}' \

    -T 'path/to/your/image.raw.gz'`,
);

const copied = ref(false);
const copyToClipboard = () => {
  copied.value = true;
  navigator.clipboard.writeText(snippet.value);
  setTimeout(() => {
    copied.value = false;
  }, 2000);
};

// upload
const uploadPlugin = inject<UploadPlugin<UploadImageMeta>>(UploadFileInjectKey);
const fileName = toRef(uploadPlugin, 'fileName');
const onSelectFile = () => {
  uploadPlugin.prepareFileInput();
};

const { createToast } = useGpToasts();
const uploadImage = async () => {
  uploadPlugin.meta.value = {
    id: props.image.id,
  };

  uploadPlugin.uploadFile(`https://${config.gateway.host}/image/upload/${props.image.id}`, {
    Authorization: `Bearer ${props.image.imageUpload.token}`,
    'Content-Type': 'application/octet-stream',
  });

  createToast({
    bodyComponent: UploadToastContent,
    timeout: 0,
    dismissible: false,
    pauseOnHover: true,
    closeButton: false,
    dismissOnBody: false,
    position: 'bottom-right',
    toastStyle: /* tw */ 'tw-bg-gp-midnight-2 tw-text-mouse tw-p-4',
  } as GpToastProps);

  emit('close');
};

const onClose = () => {
  uploadPlugin.cleanupFileUpload();

  emit('close');
};

const tab = ref<'curl' | 'file'>('curl');
const switchTab = (tabId: 'curl' | 'file') => {
  tab.value = tabId;

  if (tabId === 'curl') {
    uploadPlugin.cleanupFileUpload();
  }
};

watchEffect(() => {
  if (showRef.value) {
    switchTab('curl');
  }
});
</script>

<template>
  <GpModal
    v-model:is-opened="showRef"
    :header-icon="faCompactDisc"
    header-text="Upload project image"
    @cancel="onClose"
    @close="onClose"
    modal-body-class="tw-w-[30rem]"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <GpTabBar
          v-model="tab"
          :tabs-model="[
            { id: 'curl', label: 'cURL' },
            { id: 'file', label: 'File upload' },
          ]"
        >
          <template #item="{ isActive, item }">
            <a
              role="button"
              data-gp-element="gp-tab-entry"
              class="tw-flex tw-items-center tw-gap-2 tw-border-b tw-py-2 tw-font-bold"
              :class="{
                'tw-border-gp-cyan tw-text-gp-cyan': isActive,
                'tw-text-gp-mouse': !item.disabled && !isActive,
              }"
              @click.prevent="switchTab(item.id as 'curl' | 'file')"
              tabindex="0"
            >
              {{ item.label }}
            </a>
          </template>
        </GpTabBar>
        <transition name="fade" mode="out-in">
          <div v-if="tab === 'curl'" class="tw-mt-4">
            <h3 class="tw-mb-4">cURL</h3>
            <p>
              Please use the following cURL command to upload a new image version for this image.
            </p>
            <pre class="tw-mt-2 tw-overflow-auto">
              {{ snippet }}
            </pre>
            <div class="tw-mt-4 tw-flex tw-justify-end">
              <GpBtn
                :label="copied ? 'Copied' : 'Copy'"
                size="sm"
                :icon="copied ? faCheck : faCopy"
                @click="copyToClipboard()"
              />
            </div>
          </div>
          <div v-else-if="tab === 'file'" class="tw-mt-4">
            <h3 class="tw-mb-4">File upload</h3>
            <div class="tw-flex tw-items-center">
              <GpBtn
                class="tw-mr-4"
                :icon="faFolderOpen"
                btn-type="primary"
                outline
                size="sm"
                label="Select file"
                @click="onSelectFile"
              />
              <span v-if="fileName != null">
                {{ fileName }}
              </span>
              <span v-else>No file selected...</span>
            </div>
          </div>
        </transition>
      </div>
    </template>

    <template #footer>
      <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-4">
        <ModalFooterBtn size="sm" label="Done" @click="emit('close')" outline />
        <template v-if="tab === 'file'">
          <ModalFooterBtn
            size="sm"
            label="Upload"
            :icon="['fas', 'upload']"
            @click="uploadImage"
            btn-type="primary"
          />
        </template>
      </div>
    </template>
  </GpModal>
</template>
